/**
 * Store and send JWT when cross-origin cookies are blocked (e.g. dashboard on localhost, API on production).
 * Backend accepts both cookie and Authorization: Bearer <token>.
 */

const STORAGE_KEY = 'bxi_auth_token';

/** Set token in storage and on one or more axios instances. */
export function setAuthToken(token, ...axiosInstances) {
  if (!token) {
    clearAuthToken(...axiosInstances);
    return;
  }
  try {
    sessionStorage.setItem(STORAGE_KEY, token);
    const header = `Bearer ${token}`;
    axiosInstances.forEach((ax) => {
      if (ax?.defaults?.headers?.common) {
        ax.defaults.headers.common['Authorization'] = header;
      }
    });
  } catch (e) {
    console.warn('authToken: setAuthToken failed', e);
  }
}

/** Clear token from storage and from one or more axios instances. */
export function clearAuthToken(...axiosInstances) {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
    axiosInstances.forEach((ax) => {
      if (ax?.defaults?.headers?.common) {
        delete ax.defaults.headers.common['Authorization'];
      }
    });
  } catch (e) {
    console.warn('authToken: clearAuthToken failed', e);
  }
}

export function getStoredToken() {
  try {
    return sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * Call once on app load to restore Authorization header from session (e.g. after refresh).
 * Pass all axios instances that should send the token (e.g. default axios and v1Backend).
 */
export function initAuthFromStorage(...axiosInstances) {
  const token = getStoredToken();
  if (!token) return;
  const header = `Bearer ${token}`;
  axiosInstances.forEach((ax) => {
    if (ax?.defaults?.headers?.common) {
      ax.defaults.headers.common['Authorization'] = header;
    }
  });
}

/**
 * Extract token from common backend response shapes and initialize auth session.
 * Returns the token when present, else null.
 */
export function applyAuthFromResponse(responseData, ...axiosInstances) {
  const token =
    responseData?.token ??
    responseData?.authToken ??
    responseData?.jwt ??
    responseData?.data?.token ??
    responseData?.data?.authToken ??
    responseData?.data?.jwt ??
    responseData?.accessToken ??
    responseData?.data?.accessToken ??
    responseData?.data?.data?.token ??
    responseData?.data?.data?.authToken ??
    responseData?.data?.data?.accessToken ??
    null;

  if (!token) return null;
  setAuthToken(token, ...axiosInstances);
  return token;
}
